import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  Maximize,
  Eye,
  Shirt,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const WalkInWardrobesPage = () => {
  const walkInStyles = [
    {
      name: 'L-Shape Walk-In',
      subtitle: 'Corner Utilization',
      description: 'Maximize corner spaces with our L-shaped walk-in wardrobes. Perfect for medium bedrooms, utilizing two walls for comprehensive storage.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png',
      features: [
        'Two-wall utilization',
        'Corner hanging solutions',
        'Integrated seating options',
        'LED lighting systems',
        'Full-length mirrors',
        'Drawer tower sections'
      ],
      priceRange: 'From £3,500',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'U-Shape Walk-In',
      subtitle: 'Ultimate Storage',
      description: 'The ultimate in walk-in wardrobe luxury. Three walls of storage with central island, perfect for larger bedrooms and master suites.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: [
        'Three-wall storage',
        'Central island unit',
        'Luxury finishes available',
        'Integrated lighting zones',
        'Premium soft-close systems',
        'Bespoke internal layouts'
      ],
      priceRange: 'From £6,500',
      color: 'text-purple-600'
    },
    {
      name: 'Linear Walk-In',
      subtitle: 'Straight Line Elegance',
      description: 'Clean, linear walk-in design perfect for narrow spaces or converted spare rooms. Maximum storage in a straight-line configuration.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Single wall optimization',
        'Narrow space solutions',
        'Floor to ceiling storage',
        'Integrated bench seating',
        'Mirror wall options',
        'Adjustable shelving systems'
      ],
      priceRange: 'From £2,800',
      color: 'text-green-600'
    }
  ];

  const walkInBenefits = [
    {
      icon: Maximize,
      title: 'Ultimate Space',
      description: 'Transform spare rooms or large bedrooms into luxury dressing rooms'
    },
    {
      icon: Eye,
      title: 'Everything Visible',
      description: 'See your entire wardrobe at a glance with open storage solutions'
    },
    {
      icon: Shirt,
      title: 'Clothing Care',
      description: 'Proper hanging space and ventilation keeps clothes in perfect condition'
    },
    {
      icon: Star,
      title: 'Luxury Experience',
      description: 'Hotel-style dressing room experience in your own home'
    }
  ];

  const walkInFeatures = [
    {
      zone: 'Hanging Zones',
      options: ['Double hanging', 'Long hanging', 'Shirt sections', 'Dress areas'],
      description: 'Dedicated spaces for different clothing types'
    },
    {
      zone: 'Storage Solutions',
      options: ['Drawer towers', 'Shoe racks', 'Accessory trays', 'Hat storage'],
      description: 'Specialized storage for all wardrobe items'
    },
    {
      zone: 'Luxury Additions',
      options: ['Central islands', 'Seating areas', 'Full mirrors', 'Jewelry displays'],
      description: 'Premium touches for the ultimate dressing experience'
    },
    {
      zone: 'Lighting & Tech',
      options: ['LED strip systems', 'Motion sensors', 'USB charging', 'Climate control'],
      description: 'Modern technology integration'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Maximize className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Luxury Dressing</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Walk In Wardrobes
                <span className="block" style={{color: '#8CB89F'}}>Ultimate Luxury Storage</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> creates bespoke walk-in wardrobes that transform spaces into luxury dressing rooms. From intimate L-shapes to grand U-shape configurations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Walk-In Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Luxury Design Service
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png"
                alt="Walk In Wardrobes"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Walk-In Wardrobes?</h2>
            <p className="text-xl text-gray-600">
              Transform your space into a luxury dressing room experience
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {walkInBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Walk-In Styles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Walk-In Wardrobe Configurations</h2>
            <p className="text-xl text-gray-600">
              Choose the perfect layout for your space
            </p>
          </div>
          
          <div className="space-y-20">
            {walkInStyles.map((style, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={style.image} 
                    alt={style.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={style.color}>{style.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{style.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{style.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {style.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{style.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Walk-In Features */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Walk-In Wardrobe Zones</h2>
            <p className="text-xl text-gray-600">
              Expertly designed zones for the perfect dressing room
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {walkInFeatures.map((zone, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{zone.zone}</CardTitle>
                  <p className="text-gray-600">{zone.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {zone.options.map((option, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" style={{color: '#8CB89F'}} />
                        <span className="text-sm text-gray-600">{option}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Create Your Dream Dressing Room</h2>
          <p className="text-xl mb-8 opacity-90">
            Transform your space into a luxury walk-in wardrobe with bespoke design from Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Walk-In Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Luxury Design Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WalkInWardrobesPage;